import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface CustomAuthData {
  authenticated: boolean;
  phoneNumber: string;
  sessionToken?: string;
  userId?: string;
  expiresAt?: string;
  mustResetPassword?: boolean;
  organizationId?: string;
  organizationName?: string;
  timestamp: number;
}

export interface UserProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  phone_number: string;
  branch: string | null;
  branch_id: number | null;
  role: string | null;
  department: string | null;
  designation: string | null;
  status: string | null;
}

export const useAuth = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [user, setUser] = useState<{ phone: string } | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchUserProfile = async (phoneNumber: string) => {
    try {
      // Fetch profile by phone number
      // @ts-ignore - branch_id may not be in generated types yet
      const { data: profile, error: profileError } = await (supabase
        .from("profiles") as any)
        .select("id, full_name, email, phone_number, branch, branch_id, role, department, designation, status")
        .eq("phone_number", phoneNumber)
        .single();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        return;
      }

      if (profile) {
        // Fetch user_roles, profile_branches, and branches list in parallel BEFORE setting userProfile
        // so BranchContext finds all cached data when it first enables (avoids branchesLoading delay)
        const [rolesResult, branchesResult, branchesListResult] = await Promise.all([
          supabase.from("user_roles").select("role").eq("user_id", profile.id),
          (supabase as any).from("profile_branches").select("branch_id").eq("profile_id", profile.id),
          (supabase as any).from("branches").select("id, name, code, is_active").eq("is_active", true).order("id", { ascending: true }),
        ]);

        const { data: roleData, error: roleError } = rolesResult;
        console.log("User roles fetched:", { profileId: profile.id, roleData, roleError });

        if (roleData && roleData.length > 0) {
          const hasAdminRole = roleData.some((r) => r.role === "admin");
          console.log("Admin check:", { hasAdminRole, roles: roleData.map(r => r.role) });
          setIsAdmin(hasAdminRole);
          setUserRole(roleData[0].role);
        } else {
          console.log("No roles found for user, isAdmin will remain false");
          setIsAdmin(false);
          setUserRole(profile.role);
        }

        // Prefill React Query cache before setUserProfile so BranchContext gets data immediately
        const branchIds = (branchesResult.data || [])
          .map((row: { branch_id: number }) => row.branch_id)
          .filter((id: unknown): id is number => typeof id === "number");
        if (branchesResult.error) {
          console.warn("profile_branches fetch:", branchesResult.error);
        }
        queryClient.setQueryData(["profile-branches", profile.id], branchIds);

        if (branchesListResult.data) {
          queryClient.setQueryData(["branches"], branchesListResult.data);
        }

        setUserProfile(profile);
      }
    } catch (error) {
      console.error("Error in fetchUserProfile:", error);
    }
  };

  useEffect(() => {
    // Check for custom auth state in localStorage
    const checkAuthState = async () => {
      try {
        const authData = localStorage.getItem("customAuth");
        if (authData) {
          const parsed: CustomAuthData = JSON.parse(authData);
          
          // Check if session has expired
          if (parsed.expiresAt && new Date(parsed.expiresAt) < new Date()) {
            console.log("Session expired, logging out");
            localStorage.removeItem("customAuth");
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            return;
          }
          
          if (parsed.authenticated) {
            setUser({ phone: parsed.phoneNumber });
            setMustResetPassword(Boolean(parsed.mustResetPassword));
            await fetchUserProfile(parsed.phoneNumber);
          } else {
            setUser(null);
            setUserProfile(null);
            setMustResetPassword(false);
          }
        } else {
          setUser(null);
          setUserProfile(null);
          setMustResetPassword(false);
        }
      } catch (error) {
        console.error("Error parsing auth data:", error);
        setUser(null);
        setUserProfile(null);
        setMustResetPassword(false);
      }
      setLoading(false);
    };

    checkAuthState();

    // Listen for storage changes (e.g., login from another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "customAuth") {
        checkAuthState();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [location.pathname]);

  const signOut = useCallback(async () => {
    try {
      localStorage.removeItem("customAuth");
      setUser(null);
      setUserProfile(null);
      setUserRole(null);
      setIsAdmin(false);
      setMustResetPassword(false);

      toast.success("You've been successfully signed out");

      navigate("/login");
    } catch (error: any) {
      toast.error(error.message || "Failed to sign out");
    }
  }, [navigate]);

  return {
    user,
    userProfile,
    session: null,
    userRole,
    isAdmin,
    mustResetPassword,
    loading,
    signOut,
    isAuthenticated: !!user,
  };
};
